import React from "react";
import {
  Grid,
  TextField,
  Button,
  IconButton,
  Box,
  MenuItem,
  Typography,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import type { Produto, FormErrors } from "./types";
import { AddBoxOutlined, AddBoxRounded } from "@material-ui/icons";

interface Props {
  produtos: Produto[];
  errors: FormErrors;
  handleProdutoChange: (
    index: number,
    field: keyof Produto,
    value: string | File | string[]
  ) => void;
  adicionarProduto: () => void;
  removerProduto: (index: number) => void;
}

const Step3Produtos: React.FC<Props> = ({
  produtos,
  errors,
  handleProdutoChange,
  adicionarProduto,
  removerProduto,
}) => {
  return (
    <Box>
      {produtos.map((produto, index) => (
        <Box
          key={produto.id}
          sx={{ mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6">Produto {index + 1}</Typography>
            {produtos.length > 1 && (
              <IconButton
                onClick={() => removerProduto(index)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          {/* Primeira linha - Tipo de Produto, Tipo de Análise, Nome do Produto */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                select
                required
                label="Tipo de Produto"
                value={produto.tipo}
                onChange={(e) =>
                  handleProdutoChange(index, "tipo", e.target.value)
                }
                error={!!errors[`produto_${index}_tipo`]}
                helperText={
                  errors[`produto_${index}_tipo`] ||
                  "Selecione o tipo de produto"
                }
              >
                {/* <MenuItem value="">Selecione o tipo</MenuItem> */}
                <MenuItem value="Medicamento">Medicamento</MenuItem>
                <MenuItem value="Tecnologia de Saúde">
                  Tecnologia de Saúde
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                required
                label="Quantidade de Amostras"
                value={produto.qtdAmostraSubmetidas}
                onChange={(e) =>
                  handleProdutoChange(index, "qtdAmostraSubmetidas", e.target.value)
                }
                error={!!errors[`produto_${index}_qtdAmostraSubmetidas`]}
                helperText={
                  errors[`produto_${index}_qtdAmostraSubmetidas`] || "Quantidade de amostras"
                }
              />
            </Grid>

            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                required
                label="Nome do Produto"
                value={produto.nome}
                onChange={(e) =>
                  handleProdutoChange(index, "nome", e.target.value)
                }
                error={!!errors[`produto_${index}_nome`]}
                helperText={
                  errors[`produto_${index}_nome`] || "Nome do produto"
                }
              />
            </Grid>
          </Grid>

          {/* Segunda linha - Fabricante, País do Fabricante, Endereço do Fabricante */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                required
                label="Fabricante"
                value={produto.fabricante}
                onChange={(e) =>
                  handleProdutoChange(index, "fabricante", e.target.value)
                }
                error={!!errors[`produto_${index}_fabricante`]}
                helperText={
                  errors[`produto_${index}_fabricante`] || "Nome do fabricante"
                }
              />
            </Grid>

            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                required
                label="País do Fabricante"
                value={produto.paisFabricante}
                onChange={(e) =>
                  handleProdutoChange(index, "paisFabricante", e.target.value)
                }
                error={!!errors[`produto_${index}_paisFabricante`]}
                helperText={
                  errors[`produto_${index}_paisFabricante`] ||
                  "País de origem do fabricante"
                }
              />
            </Grid>

            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                required
                label="Endereço do Fabricante"
                value={produto.enderecoFabricante}
                onChange={(e) =>
                  handleProdutoChange(
                    index,
                    "enderecoFabricante",
                    e.target.value
                  )
                }
                error={!!errors[`produto_${index}_enderecoFabricante`]}
                helperText={
                  errors[`produto_${index}_enderecoFabricante`] ||
                  "Endereço completo do fabricante"
                }
              />
            </Grid>
          </Grid>

          {/* Terceira linha - Lote, Data de Fabrico, Data de Validade, Dosagem/Concentração */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                required
                label="Lote"
                value={produto.lote}
                onChange={(e) =>
                  handleProdutoChange(index, "lote", e.target.value)
                }
                error={!!errors[`produto_${index}_lote`]}
                helperText={errors[`produto_${index}_lote`] || "Número do lote"}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                required
                label="Data de Fabrico"
                type="date"
                value={produto.dataFabrico}
                onChange={(e) =>
                  handleProdutoChange(index, "dataFabrico", e.target.value)
                }
                error={!!errors[`produto_${index}_dataFabrico`]}
                helperText={
                  errors[`produto_${index}_dataFabrico`] ||
                  "Data de fabricação do produto"
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                required
                label="Data de Validade"
                type="date"
                value={produto.dataValidade}
                onChange={(e) =>
                  handleProdutoChange(index, "dataValidade", e.target.value)
                }
                error={!!errors[`produto_${index}_dataValidade`]}
                helperText={
                  errors[`produto_${index}_dataValidade`] ||
                  "Data de validade do produto"
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            {produto.tipo === "Medicamento" &&
              <>
                <Grid item xs={12} sm={4} md={4} lg={4}>
                  <TextField
                    fullWidth
                    required
                    label="Dosagem/Concentração"
                    value={produto.dosagemConcentracao}
                    onChange={(e) =>
                      handleProdutoChange(
                        index,
                        "dosagemConcentracao",
                        e.target.value
                      )
                    }
                    error={!!errors[`produto_${index}_dosagemConcentracao`]}
                    helperText={
                      errors[`produto_${index}_dosagemConcentracao`] ||
                      "Ex: 500mg, 10ml, etc."
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4} lg={4}>
                  <TextField
                    fullWidth
                    required
                    label="Forma Farmacêutica"
                    value={produto.formaFarmaceutica}
                    onChange={(e) =>
                      handleProdutoChange(
                        index,
                        "formaFarmaceutica",
                        e.target.value
                      )
                    }
                    error={!!errors[`produto_${index}_formaFarmaceutica`]}
                    helperText={
                      errors[`produto_${index}_formaFarmaceutica`] ||
                      "Ex: Comprimido, Xarope, Injetável"
                    }
                  />
                </Grid>
              </>
            }
            {produto.tipo === "Tecnologia de Saúde" &&
              <>
                <Grid item xs={12} sm={4} md={4} lg={4}>
                  <TextField
                    fullWidth
                    required
                    label="Tipo"
                    // value={produto?.tipoTecnologiaSaude}
                    // onChange={(e) =>
                    //   handleProdutoChange(
                    //     index,
                    //     "tipoTecnologiaSaude",
                    //     e.target.value
                    //   )
                    // }
                    error={!!errors[`produto_${index}Tecnologia de Saúde`]}
                    helperText={
                      errors[`produto_${index}Tecnologia de Saúde`] ||
                      "Ex: Equipamento Médico, Dispositivo de Diagnóstico"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4} lg={4}>
                  <TextField
                    fullWidth
                    required
                    label="Especificação"
                    // value={produto?.especificacaoTecnologiaSaude}
                    // onChange={(e) =>
                    //   handleProdutoChange(
                    //     index,
                    //     "especificacaoTecnologiaSaude",
                    //     e.target.value
                    //   )
                    // }
                    error={!!errors[`produto_${index}especificacaoTecnologiaSaude`]}
                    helperText={
                      errors[`produto_${index}especificacaoTecnologiaSaude`] ||
                      "Ex: Modelo X, Versão Y"
                    }
                  />
                </Grid>
              </>
            }
          </Grid>

          {/* Quinta linha - Forma Farmacêutica + Receita Médica (se aplicável) */}

          {/* <InputDocUpload
              fullWidth={true}
              imputLigth={true}
              onFileSelect={handleInputDocUploadChange}
              inputName="receitaMedica"
              inputTitle="Receita Médica"
              inputDescription="Anexar receita médica (PDF, JPG, PNG)
            /> */}
          <Grid container spacing={2}>
            {produto.tipo === "Medicamento" && (
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <TextField
                  type="file"
                  label="Receita Médica"
                  InputLabelProps={{ shrink: true }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleProdutoChange(index, "receitaMedica", file);
                    }
                  }}
                  error={!!errors[`produto_${index}_receitaMedica`]}
                  helperText={
                    errors[`produto_${index}_receitaMedica`] ||
                    "Anexar receita médica (PDF, JPG, PNG)"
                  }
                  inputProps={{
                    accept: ".pdf,.jpg,.jpeg,.png",
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      ))}

      <Box sx={{ mt: 3, display: "flex" }}>
        <Button variant="text" onClick={adicionarProduto} size="large">
          Adicionar mais Produto {" "}
          {/* {"(" + produtos.length + ")"} */}
          <AddBoxRounded />
        </Button>
      </Box>
    </Box>
  );
};

export default Step3Produtos;
